// InterviewAssistant.jsx
import { Pause, PlayArrow, Stop } from "@mui/icons-material";
import { Box, Button, Grid, Typography } from "@mui/material";
import Vapi from "@vapi-ai/web";
import { GET_QUESTIONNAIRE_CONTEXT } from "adapters/form.service";
import { useEffect, useRef, useState } from "react";
import { parseRawTranscript } from "screens/helpers/transcriptParser";
import FormSelectionDialog from "screens/UIComponents/FormSelectionDialog";
import { v4 as uuidv4 } from "uuid";

export default function InterviewAssistant({
  publicKey,
  assistantId,
  initialVars = { candidateName: "Ravi", position: "Software Engineer" },
}) {
  const vapiRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const canvasRef = useRef(null);

  const messagesEndRef = useRef(null);

  const [running, setRunning] = useState(false);
  const [messages, setMessages] = useState([]); // {id, role, text, isFinal}
  const [rawEvents, setRawEvents] = useState([]); // debug
  const [error, setError] = useState("");
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(false);

  const [previousTranscript, setPreviousTranscript] = useState([]);

  const [openDialog, setOpenDialog] = useState(false);

  const sessionId = localStorage.getItem("sessionId");

  const [formData, setFormData] = useState({
    full_name: "",
    date_of_birth: "",
    current_address: "",
    medications: false,
    medications_list: "",
    recent_hospital_visit: false,
    health_conditions: false,
    health_conditions_list: "",
    schedule_appointment: false,
    appointment_details: "",
  });

  // console.log("messages---", messages);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Initialize Vapi client once
  useEffect(() => {
    if (!publicKey) {
      setError("Missing publicKey prop");
      return;
    }
    try {
      vapiRef.current = new Vapi(publicKey);
      // global error handler
      vapiRef.current.on("error", (e) => {
        console.error("Vapi error:", e);
        setError(prettyErr(e));
      });
    } catch (e) {
      setError(prettyErr(e));
    }

    return () => {
      // cleanup on unmount
      try {
        vapiRef.current?.stop?.();
      } catch {}
      stopVisualizer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicKey]);

  // Utility: stable id
  const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  // Pretty-print errors
  function prettyErr(e) {
    try {
      if (!e) return "";
      if (typeof e === "string") return e;
      if (e.message) return e.message;
      return JSON.stringify(e, Object.getOwnPropertyNames(e), 2);
    } catch {
      return String(e);
    }
  }

  // Push or update a transcript entry (supports partial updates)
  function pushTranscript(role, text, isFinal = false) {
    if (!text && !isFinal) return;
    setMessages((prev) => {
      // if partial and last is same role & not final => update
      if (!isFinal && prev.length) {
        const last = prev[prev.length - 1];
        if (last && last.role === role && !last.isFinal) {
          const copy = prev.slice();
          copy[copy.length - 1] = { ...last, text };
          return copy;
        }
      }
      // if final and last is same role & not final => finalize
      if (isFinal && prev.length) {
        const last = prev[prev.length - 1];
        if (last && last.role === role && !last.isFinal) {
          const copy = prev.slice();
          copy[copy.length - 1] = { ...last, text, isFinal: true };
          return copy;
        }
      }
      // ✅ Prevent duplicates: check if exact same role+text+isFinal already exists
      const exists = prev.some(
        (m) => m.role === role && m.text === text && m.isFinal === isFinal
      );
      if (exists) return prev;

      // otherwise push new
      return [...prev, { id: genId(), role, text: text || "", isFinal }];
    });
  }

  // Parse many shapes of SDK messages and derive text/role/isFinal
  function handleIncomingEvent(eventName, msg) {
    // keep raw events for debugging
    setRawEvents((r) => [
      { id: genId(), time: new Date().toISOString(), eventName, msg },
      ...r.slice(0, 199),
    ]);

    // Defensive parsing - look for many possible keys
    try {
      // common direct cases
      if (!msg) return;

      // 1) msg.type === 'transcript'
      if (msg.type === "transcript" || msg.transcript || msg.transcriptType) {
        const role =
          msg.role ||
          (msg.from
            ? msg.from === "user"
              ? "User"
              : "Assistant"
            : msg.isUser
            ? "User"
            : "Assistant");
        const text =
          msg.transcript || msg.text || msg.payload?.text || msg.content || "";
        const isFinal =
          msg.transcriptType === "final" ||
          msg.isFinal === true ||
          eventName === "speech.final" ||
          eventName === "transcript.final";
        pushTranscript(role === "user" ? "User" : "Assistant", text, isFinal);
        return;
      }

      // 2) streaming assistant text deltas
      if (
        eventName === "response.output_text.delta" ||
        eventName === "response.delta"
      ) {
        const text =
          typeof msg === "string" ? msg : msg.delta || msg.text || msg;
        pushTranscript("Assistant", String(text || ""), false);
        return;
      }
      if (eventName === "response.output_text.done") {
        // finalize last assistant
        setMessages((prev) => {
          if (!prev.length) return prev;
          const last = prev[prev.length - 1];
          if (last.role === "Assistant" && !last.isFinal) {
            const copy = prev.slice();
            copy[copy.length - 1] = { ...last, isFinal: true };
            return copy;
          }
          return prev;
        });
        return;
      }

      // 3) events with msg.role and msg.content (array)
      if (msg.role && msg.content) {
        const role = msg.role === "user" ? "User" : "Assistant";
        let text = "";
        if (Array.isArray(msg.content)) {
          // try find text entries
          const t = msg.content
            .map((c) => c?.text || (typeof c === "string" ? c : ""))
            .filter(Boolean)
            .join(" ");
          text = t || JSON.stringify(msg.content);
        } else if (typeof msg.content === "string") {
          text = msg.content;
        }
        pushTranscript(role, text, true);
        return;
      }

      // 4) speech.* events
      if (/^speech(\.|:)/.test(eventName) || eventName.startsWith("speech")) {
        const text = msg.text || msg.transcript || msg;
        const role = msg.role === "assistant" ? "Assistant" : "User";
        const isFinal = eventName.includes("final") || msg.isFinal === true;
        pushTranscript(role, String(text || ""), isFinal);
        return;
      }

      // 5) fallback: if msg is string, treat as assistant
      if (typeof msg === "string") {
        pushTranscript("Assistant", msg, true);
        return;
      }

      //testing
      if (eventName === "message" && msg?.type === "tool-calls") {
        // setFormData({...formData, ...msg?.toolCallList?.[0]?.function?.arguments})
        setFormData((prevState) => ({
          ...prevState,
          ...msg?.toolCallList?.[0]?.function?.arguments,
        }));
      }
      //testing

      // 6) last resort: stringify small msg for debug but don't spam transcripts
      // console.debug("Unhandled Vapi message shape:", eventName, msg);
    } catch (e) {
      console.error("handleIncomingEvent parse failed:", e, msg);
    }
  }

  // Attach listeners for many event names (defensive)
  function attachListeners() {
    const vapi = vapiRef.current;
    if (!vapi) return;
    const events = [
      "message",
      "transcript",
      "speech.partial",
      "speech.final",
      "speech.input",
      "speech.output",
      "response.output_text.delta",
      "response.output_text.done",
      "volume",
      "volume-level",
      "call-start",
      "call-end",
      "error",
    ];

    events.forEach((ev) => {
      try {
        vapi.on(ev, (msg) => handleIncomingEvent(ev, msg));
      } catch (e) {
        // some SDK builds may not support all events; ignore attach errors
        console.warn("vapi.on attach failed for", ev, e);
      }
    });

    // also catch generic 'message' shape already above
    try {
      vapi.on("message", (msg) => handleIncomingEvent("message", msg));
    } catch {}
  }

  // Detach listeners helper (best-effort)
  function detachListeners() {
    try {
      const vapi = vapiRef.current;
      if (!vapi) return;
      // try to remove many events if off() exists
      const events = [
        "message",
        "transcript",
        "speech.partial",
        "speech.final",
        "speech.input",
        "speech.output",
        "response.output_text.delta",
        "response.output_text.done",
        "volume",
        "volume-level",
        "call-start",
        "call-end",
        "error",
      ];
      events.forEach((ev) => {
        if (vapi.off) vapi.off(ev);
      });
    } catch {}
  }

  // Visualizer: start analyser and drawing loop
  async function startVisualizer() {
    try {
      // Request mic (so browser prompts). Keep stream for both visualizer and Vapi SDK.
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = s;

      audioCtxRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      sourceRef.current = audioCtxRef.current.createMediaStreamSource(s);

      analyserRef.current = audioCtxRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
      sourceRef.current.connect(analyserRef.current);

      const bufferLength = analyserRef.current.fftSize;
      const dataArray = new Uint8Array(bufferLength);

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      function draw() {
        rafRef.current = requestAnimationFrame(draw);
        analyserRef.current.getByteTimeDomainData(dataArray);

        // compute RMS for volume (0..1)
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          const val = dataArray[i] - 128;
          sum += val * val;
        }
        const rms = Math.sqrt(sum / bufferLength) / 128;

        // draw circular ring scaled by rms
        const w = canvas.width;
        const h = canvas.height;
        const cx = w / 2;
        const cy = h / 2;
        const baseR = Math.min(w, h) / 4;
        const pulseR = baseR + rms * baseR * 0.9;

        ctx.clearRect(0, 0, w, h);

        // outer background ring
        ctx.beginPath();
        ctx.arc(cx, cy, baseR + baseR * 0.2, 0, Math.PI * 2);
        ctx.fillStyle = "#f3f4f6";
        ctx.fill();

        // pulse ring
        ctx.beginPath();
        ctx.arc(cx, cy, pulseR, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(79, 70, 229, 0.18)"; // soft violet
        ctx.fill();

        // center circle
        ctx.beginPath();
        ctx.arc(cx, cy, baseR * 0.6, 0, Math.PI * 2);
        ctx.fillStyle = running ? "#4f46e5" : "#9ca3af";
        ctx.fill();
      }

      draw();
    } catch (e) {
      console.error("Visualizer start failed:", e);
      setError(prettyErr(e));
    }
  }

  function stopVisualizer() {
    try {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (analyserRef.current) analyserRef.current.disconnect?.();
      if (sourceRef.current) sourceRef.current.disconnect?.();
      if (audioCtxRef.current) audioCtxRef.current.close();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    } catch (e) {
      console.warn("stopVisualizer error:", e);
    } finally {
      analyserRef.current = null;
      sourceRef.current = null;
      audioCtxRef.current = null;
      streamRef.current = null;
    }
  }

  // Start Vapi session (using assistantId + assistant_overrides)
  async function startSession() {
    const tempSessionId = sessionId || uuidv4();
    setLoading(true);
    setError("");
    setMessages([]);
    try {
      if (!vapiRef.current) {
        setError("Vapi client not initialized (check publicKey).");
        return;
      }

      attachListeners();

      // start visualizer first so browser prompts mic permission
      // await startVisualizer();

      // call start with assistantId (first arg) and assistant_overrides second
      // note: SDK shapes vary; this is the front-end method that works with publicKey + assistantId
      const wsResponse = await vapiRef.current.start(assistantId
        , {
        // assistant_overrides: {
        // variableValues: initialVars,
        variableValues: {
          // previous_transcript: previousTranscript,
          ...initialVars,
        // metadata: {
        //   session_id: tempSessionId,
        // },
        // ask for transcripts to be forwarded to client
        //   clientMessages:
        //     "conversation-update,transcript,response.output_text.delta,speech-update,voice-input,model-output,status-update",
        },
      }
    );

      setSocket(wsResponse);

      if (wsResponse?.id) {
        localStorage.setItem(
          "sessionId",
          wsResponse?.assistantOverrides?.metadata?.session_id
        );
      }

      setRunning(true);
    } catch (e) {
      console.error("vapi.start failed:", e);
      setError(prettyErr(e));
      // try to cleanup partially started things
      try {
        await vapiRef.current?.stop?.();
      } catch {}
      stopVisualizer();
      detachListeners();
    } finally {
      setLoading(false);
    }
  }

  // Stop session
  async function stopSession() {
    try {
      await vapiRef.current?.stop?.();
    } catch (e) {
      console.warn("stop error", e);
    }
    stopVisualizer();
    detachListeners();
    setRunning(false);
  }

  // After renderMessage function (newly modified)

  // Replace your renderMessage function with this:
  const renderMessage = (m) => (
    <div
      key={m.id}
      style={{
        display: "flex",
        justifyContent: m.role === "User" ? "flex-end" : "flex-start",
        marginBottom: 8,
      }}
    >
      <div
        style={{
          maxWidth: "70%", // bubble width
          padding: "10px 14px",
          borderRadius: 16,
          background: m.role === "User" ? "#4f46e5" : "#f3f4f6",
          color: m.role === "User" ? "white" : "#111827",
          borderBottomRightRadius: m.role === "User" ? 4 : 16,
          borderBottomLeftRadius: m.role === "Assistant" ? 4 : 16,
          fontSize: 14,
        }}
      >
        {m.role === "Assistant" && !m.isFinal ? (
          <TypewriterMessage text={m.text} />
        ) : (
          <div style={{ whiteSpace: "pre-wrap" }}>{m.text}</div>
        )}
      </div>
    </div>
  );

  useEffect(() => {
    if (sessionId && !running && !socket?.id) {
      handleDialogOpen();
    }
  }, [sessionId, running, socket]);

  const handleDialogOpen = () => setOpenDialog(true);
  const handleDialogClose = () => setOpenDialog(false);

  const getQuestionnaireContext = async () => {
    setLoading(true);
    try {
      const res = await GET_QUESTIONNAIRE_CONTEXT({
        id: sessionId,
        tenantId: "cr-ai-research-h7igh",
      });
      const resPreviousTranscript =
        res?.data?.questionnaire_responses?.[0]?.meta?.extension?.find(
          (f) =>
            f?.url ===
            "http://demo.orie.ai/fhir/extensions/conversation-transcript"
        )?.valueString;

      if (resPreviousTranscript?.split("rawTranscript:")?.[1]) {
        setPreviousTranscript(parseRawTranscript(resPreviousTranscript));
      }
    } catch (e) {
      console.error("error: ", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* <AutomaticForm formData={formData}/> */}

      <FormSelectionDialog
        open={openDialog}
        onClose={handleDialogClose}
        // dialogTitle="Partially filled form found !"
        dialogTitle="Resume or Start a New Form"
        dialogContent={
          <>
            <Typography gutterBottom className="mb-4">
              {/* A partially filled form found for the current user, would you to like to continue from where you had left or start filling new form ? */}
              We found a partially filled form for this user. Would you like to
              continue from where you left off or start a new form from scratch?
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                getQuestionnaireContext();
                handleDialogClose();
              }}
              style={{ marginBottom: "1rem" }}
              className="rounded-3 "
            >
              Resume Partially Filled Form
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                localStorage.removeItem("sessionId");
                handleDialogClose();
              }}
              className="rounded-3"
            >
              Start New Form
            </Button>
          </>
        }
      />

      <div
        style={{
          // maxWidth: 820,
          // margin: "18px auto",
          fontFamily: "Inter, system-ui, sans-serif",
          // border: "1px solid red",
          height: "100%",
        }}
        className="d-flex flex-column"
      >
        {/* {error && (
        <div style={{ marginTop: 14, padding: 10, background: "#fff4f4", color: "#9b1c1c", borderRadius: 8 }}>
          <strong>Error:</strong> <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{error}</pre>
        </div>
      )} */}

        <div className="p-2 w-100 flex-grow-1 overflow-auto">
          <div className="h-100 d-flex flex-column">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h4 style={{ margin: 0 }}>Conversation</h4>
              {/* <div style={{ fontSize: 12, color: "#6b7280" }}>{messages.length} lines</div> */}
              <div>
                <strong>Status:</strong>{" "}
                {running ? "Connected" : "Not Connected"}
              </div>
            </div>

            <div
              className="flex-grow-1 overflow-auto border border-sm rounded-4 p-1"
              style={{
                background: "#fafafa",
                borderRadius: 8,
                overflowY: "auto",
                // border: "1px solid red",
                display: messages.length === 0 ? "flex" : "",
                alignItems: messages.length === 0 ? "center" : "",
                justifyContent: messages.length === 0 ? "center" : "",
              }}
            >
              {messages.length === 0 ? (
                <div style={{ color: "#9ca3af" }}>
                  No transcripts yet — press Start and allow mic.
                </div>
              ) : (
                messages.map(renderMessage)
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* visualizer */}
            {/* <canvas ref={canvasRef} width={120} height={120} style={{ borderRadius: 999 }} /> */}
            {/* visualizer */}
          </div>
        </div>

        <div className="w-100">
          {!running ? (
            <Box className="px-2 pb-1">
              <Button
                startIcon={<PlayArrow />}
                className="text-capitalize  rounded-4"
                sx={{ fontSize: "0.8rem", background: "#4f46e5" }}
                fullWidth
                variant="contained"
                onClick={startSession}
                loading={loading}
              >
                {previousTranscript?.length > 0 ? "Resume" : "Start (mic)"}
              </Button>
            </Box>
          ) : (
            // <Grid container spacing={1} className="mx-2">
            //   <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
            //     <Button
            //       onClick={() => {
            //         if (vapiRef.current.isMuted()) {
            //           vapiRef.current.setMuted(false);
            //         } else {
            //           vapiRef.current.setMuted(true);
            //         }
            //       }}
            //       startIcon={
            //         vapiRef.current.isMuted() ? <PlayArrow /> : <Pause />
            //       }
            //       className="text-capitalize p-2 rounded-4"
            //       sx={{ fontSize: "0.8rem" }}
            //       fullWidth
            //       variant="contained"
            //     >
            //       {/* {vapiRef.current.isMuted() ? "Unmute" :"Mute"} */}
            //       {vapiRef.current.isMuted() ? "Resume" : "Pause"}
            //     </Button>
            //   </Grid>

            //   <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
            //     <Button
            //       startIcon={<Stop />}
            //       className="text-capitalize p-2 rounded-4 text-white"
            //       sx={{ fontSize: "0.8rem", background: "#ef4444" }}
            //       fullWidth
            //       variant="outlined"
            //       onClick={stopSession}
            //     >
            //       End
            //     </Button>
            //   </Grid>
            // </Grid>
            <Box className="px-2 pb-1">
              <Button
                startIcon={<Stop />}
                className="text-capitalize p-2 rounded-4 text-white"
                sx={{ fontSize: "0.8rem", background: "#ef4444" }}
                fullWidth
                variant="outlined"
                onClick={stopSession}
              >
                End
              </Button>
            </Box>
          )}
        </div>
        <style>
          {`
    @keyframes blink-cursor {
      from, to { opacity: 0; }
      50% { opacity: 1; }
    }
  `}
        </style>
      </div>
    </>
  );
}

// Helper: replacer that avoids circular JSON crash
function replacerForSafe() {
  const seen = new WeakSet();
  return function replacer(key, value) {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) return "[Circular]";
      seen.add(value);
    }
    // If value is an Error-like object, show useful props
    if (value instanceof Error) {
      const err = {};
      Object.getOwnPropertyNames(value).forEach((k) => (err[k] = value[k]));
      return err;
    }
    return value;
  };
}

// Add this new component right after stopSession()
// Renders a message with or without the streaming effect
const TypewriterMessage = ({ text }) => {
  const [visibleText, setVisibleText] = useState("");
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (index < text.length) {
      const timeout = setTimeout(() => {
        setVisibleText((prev) => prev + text.charAt(index));
        setIndex((prev) => prev + 1);
      }, 15); // Adjust typing speed here (in ms)
      return () => clearTimeout(timeout);
    }
  }, [index, text]);

  return (
    <span style={{ whiteSpace: "pre-wrap", color: "#111827" }}>
      {visibleText}
      {index < text.length && (
        <span style={{ animation: "blink-cursor 0.75s step-end infinite" }}>
          |
        </span>
      )}
    </span>
  );
};
